import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { LabelBold, Box } from '@comeon/ui';
import { selectLocale } from '~/components/account/ducks';
import useTranslations from '../../../translation/use-translations';
import { WE_SPIN_NEXT_SESSION_TEXT_KEYS } from '../constants/we-spin-text-keys';
import WeSpinGameRoomNextSessionItem from './we-spin-game-room-next-session-item';
import { selectSessionForStreamRef } from '../ducks/we-spin-selectors';
import useContextualPathTransformer from '../../../../hooks/use-contextual-path-transformer';
import { RUNNING, UPCOMING_STATES, PAST_STATES } from '../constants/we-spin-stream-states';
import { ROUTE_WE_SPIN_GAME_VIEW } from '../constants/we-spin-route-constants';
import { WE_SPIN_EVENT_LIST_LABEL_DATA_AT } from '../constants/we-spin-data-at-attributes';
import { streamToGameRoomRoute } from '../utils/stream-to-game-room-route';
import { getWeekDayDateAndMonth } from '../utils/we-spin-time-helper';
import WeSpinShare from '../components/we-spin-share';

const WeSpinGameRoomEventList = ({ streamView, streamRef, resources }) => {
	const navigate = useNavigate();
	const session = useSelector(selectSessionForStreamRef(streamRef));
	const transformPath = useContextualPathTransformer();
	const onClickLink = clickedStreamView => {
		const link = transformPath(
			clickedStreamView.state === RUNNING
				? `${ROUTE_WE_SPIN_GAME_VIEW}/${session.sessionRef}`
				: streamToGameRoomRoute(clickedStreamView)
		);
		navigate(link);
	};

	const { translations: eventListTranslations, isLoadingTranslations } =
		useTranslations(WE_SPIN_NEXT_SESSION_TEXT_KEYS);

	const upcomingEvents =
		session?.streamViews
			.filter(sv => UPCOMING_STATES.includes(sv.state))
			.sort((a, b) => new Date(a.start) - new Date(b.start)) || [];

	const endedEvents =
		session?.streamViews
			.filter(sv => PAST_STATES.includes(sv.state))
			.sort((a, b) => new Date(a.start) - new Date(b.start)) || [];

	const locale = useSelector(selectLocale);

	const currentDateText = getWeekDayDateAndMonth(streamView.start, eventListTranslations);

	if (isLoadingTranslations) return null;

	return (
		<>
			{upcomingEvents && upcomingEvents.length > 0 && (
				<>
					<div className="wespin-eventlist-date u-mt2">
						<LabelBold className="u-pl1" data-at={WE_SPIN_EVENT_LIST_LABEL_DATA_AT}>
							{currentDateText}
						</LabelBold>
					</div>
					<div className="u-p1 wespin-game-room-eventlist-upcoming-events">
						{upcomingEvents.map(sv => (
							<Box mb="5" key={sv.streamRef}>
								<WeSpinGameRoomNextSessionItem
									key={sv.streamRef}
									locale={locale}
									stream={sv}
									translations={eventListTranslations}
									showGameIcon
									showStartTime={false}
									onClick={e => {
										e.preventDefault();
										e.stopPropagation();
										onClickLink(sv);
									}}
									resources={resources}
								/>
							</Box>
						))}
					</div>
				</>
			)}
			{endedEvents && endedEvents.length > 0 && (
				<>
					{upcomingEvents.length > 0 && (
						<div>
							<LabelBold className="u-pl1">{eventListTranslations.WE_SPIN_EVENT_LIST_ENDED}</LabelBold>
						</div>
					)}
					<div className="u-p1">
						{endedEvents.map(sv => (
							<Box mt="3" mb="5" key={sv.streamRef}>
								<WeSpinGameRoomNextSessionItem
									key={sv.streamRef}
									locale={locale}
									stream={sv}
									translations={eventListTranslations}
									showGameIcon
									showStartTime={false}
									onClick={e => {
										e.preventDefault();
										e.stopPropagation();
										onClickLink(sv);
									}}
									resources={resources}
								/>
							</Box>
						))}
					</div>
				</>
			)}
			{upcomingEvents.length > 0 && (
				<WeSpinShare sessionRef={streamRef} startTime={streamView.start} streamerName={streamView.name} />
			)}
		</>
	);
};

export default WeSpinGameRoomEventList;
